/**
 * OpportunitiesTab v3.1 - Persona-Continuity + Evidence-Quality + Safe Strategy Summary
 * 
 * Features:
 * - Core / Adjacent / Edge tier groupings
 * - Persona filter toggle above campaigns
 * - Evidence quality indicators (strong/medium/weak)
 * - Safe action type strategy summary
 * - Missing personas generate campaigns
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Loader2, Sparkles, ChevronDown, ChevronRight, Target, Beaker, 
  AlertTriangle, FileText, Users, Zap, CheckCircle, XCircle, BookmarkPlus,
  Star, TrendingUp, Lightbulb, Info, ListChecks, ArrowRight, BadgeCheck,
  CircleDot, UserPlus, Megaphone, Plus, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OpportunitiesTab({ assetId }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [expandedOpps, setExpandedOpps] = useState({});
  const [activeTab, setActiveTab] = useState('core');
  const [selectedPersona, setSelectedPersona] = useState('all'); // Persona filter

  useEffect(() => {
    loadOpportunities();
  }, [assetId]);

  const loadOpportunities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/assets/${assetId}/opportunities`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateOpportunities = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/api/assets/${assetId}/opportunities/generate`, {}, {
        timeout: 120000 // 2 minute timeout for 6-agent pipeline
      });
      setData(response.data);
      if (response.data?.status === 'completed') {
        toast.success('Opportunities generated!');
      } else if (response.data?.status === 'failed') {
        toast.error('Generation failed: ' + (response.data?.errors?.[0] || 'Unknown error'));
      }
    } catch (error) {
      console.error('Generate error:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to generate';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const addToBucketList = async (item) => {
    try {
      await axios.post(`${API}/api/bucket-list/add`, item);
      toast.success('Added to Potential Campaigns!');
    } catch (error) {
      toast.error('Failed');
    }
  };

  const toggleOpp = (id) => {
    setExpandedOpps(prev => {
      const newState = { ...prev };
      newState[id] = !newState[id];
      return newState;
    });
  };

  // Build persona list for filter (must be before any early returns due to rules of hooks)
  const personasExisting = data?.personas_existing || data?.business_snapshot?.personas_existing || [];
  const personasMissing = data?.personas_missing || data?.business_snapshot?.personas_missing || [];
  
  const allPersonas = useMemo(() => {
    const personas = [];
    personasExisting.forEach(p => {
      personas.push({ type: 'existing', name: p.persona_name || p.name });
    });
    personasMissing.forEach(p => {
      personas.push({ type: 'missing', name: p.persona_name || p.name });
    });
    return personas;
  }, [personasExisting, personasMissing]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check data format
  const opportunities = data?.opportunities || {};
  const isNewFormat = opportunities.core !== undefined || opportunities.adjacent !== undefined;
  const isV3 = data?.version === 'v3' || data?.version === 'v3.1';
  
  const coreOpps = isNewFormat ? (opportunities.core || []) : [];
  const adjacentOpps = isNewFormat ? (opportunities.adjacent || []) : [];
  const edgeOpps = isNewFormat ? (opportunities.edge || []) : [];
  const flatOpps = !isNewFormat && Array.isArray(data?.opportunities) ? data.opportunities : [];
  
  const hasOpportunities = coreOpps.length > 0 || adjacentOpps.length > 0 || edgeOpps.length > 0 || flatOpps.length > 0;

  // V3.1 specific data
  const strategySummary = data?.strategy_summary;
  const campaignsByPersona = data?.campaigns_by_persona || {};
  const businessSnapshot = data?.business_snapshot;

  // Filter opportunities by selected persona
  const filterByPersona = (opps) => {
    if (selectedPersona === 'all') return opps;
    return opps.filter(opp => {
      const ref = opp.persona_ref || opp.anchor_persona;
      if (!ref) return false;
      const personaName = typeof ref === 'string' ? ref : ref.persona_name;
      return personaName === selectedPersona;
    });
  };

  const filteredCore = filterByPersona(coreOpps);
  const filteredAdjacent = filterByPersona(adjacentOpps);
  const filteredEdge = filterByPersona(edgeOpps);

  return (
    <div className="space-y-6">
      <Header 
        hasOpportunities={hasOpportunities}
        generating={generating}
        onGenerate={generateOpportunities}
      />

      {!hasOpportunities && (
        <EmptyState generating={generating} onGenerate={generateOpportunities} />
      )}

      {/* V3.1: Strategy Summary (Safe Action Types) */}
      {hasOpportunities && strategySummary && (
        <StrategySummaryPanel summary={strategySummary} />
      )}

      {/* V3.1: Persona Coverage */}
      {hasOpportunities && isV3 && (personasExisting.length > 0 || personasMissing.length > 0) && (
        <PersonaCoveragePanel existing={personasExisting} missing={personasMissing} campaignsByPersona={campaignsByPersona} />
      )}

      {/* Persona Filter */}
      {hasOpportunities && allPersonas.length > 0 && (
        <PersonaFilter 
          personas={allPersonas}
          selected={selectedPersona}
          onSelect={setSelectedPersona}
        />
      )}

      {/* V3.1: Campaign Plan */}
      {hasOpportunities && isV3 && (
        <CampaignPlanPanel 
          opps={filterByPersona([...coreOpps, ...adjacentOpps, ...edgeOpps])} 
          selectedPersona={selectedPersona}
        />
      )}

      {data?.warnings?.length > 0 && (
        <WarningsDisplay warnings={data.warnings} />
      )}

      {hasOpportunities && isNewFormat && (
        <TieredOpportunities
          coreOpps={filteredCore}
          adjacentOpps={filteredAdjacent}
          edgeOpps={filteredEdge}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          expandedOpps={expandedOpps}
          toggleOpp={toggleOpp}
          addToBucketList={addToBucketList}
          assetId={assetId}
          data={data}
        />
      )}

      {hasOpportunities && !isNewFormat && (
        <FlatOpportunities
          opportunities={flatOpps}
          expandedOpps={expandedOpps}
          toggleOpp={toggleOpp}
          addToBucketList={addToBucketList}
          assetId={assetId}
          data={data}
        />
      )}
    </div>
  );
}

// =============================================================================
// PERSONA FILTER
// =============================================================================

function PersonaFilter({ personas, selected, onSelect }) {
  return (
    <div className="flex items-center gap-2 flex-wrap p-3 bg-muted/30 rounded-lg">
      <Filter className="w-4 h-4 text-muted-foreground" />
      <span className="text-sm text-muted-foreground mr-2">Filter by persona:</span>
      <button
        onClick={() => onSelect('all')}
        className={`px-3 py-1 rounded-full text-sm transition-colors ${
          selected === 'all' 
            ? 'bg-primary text-primary-foreground' 
            : 'bg-muted hover:bg-muted/80 text-foreground'
        }`}
      >
        All personas
      </button>
      {personas.map((p, i) => (
        <button
          key={i}
          onClick={() => onSelect(p.name)}
          className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center gap-1 ${
            selected === p.name 
              ? 'bg-primary text-primary-foreground' 
              : p.type === 'missing'
                ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-700'
                : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
        >
          {p.type === 'missing' && <UserPlus className="w-3 h-3" />}
          {p.name}
        </button>
      ))}
    </div>
  );
}

function Header({ hasOpportunities, generating, onGenerate }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Opportunities</h3>
        <p className="text-sm text-muted-foreground">AI-generated campaign opportunities with evidence-based scoring</p>
      </div>
      {hasOpportunities && (
        <Button onClick={onGenerate} disabled={generating} className="gap-2">
          {generating && <Loader2 className="w-4 h-4 animate-spin" />}
          {!generating && <Sparkles className="w-4 h-4" />}
          Regenerate
        </Button>
      )}
    </div>
  );
}

function EmptyState({ generating, onGenerate }) {
  return (
    <div className="text-center py-12 bg-muted/30 rounded-xl border border-border">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
        <Target className="w-8 h-8 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">No Opportunities Yet</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Generate AI-powered campaign opportunities with calibrated confidence scoring.
      </p>
      <Button onClick={onGenerate} disabled={generating} className="gap-2">
        {generating && <Loader2 className="w-4 h-4 animate-spin" />}
        {!generating && <Sparkles className="w-4 h-4" />}
        Generate Opportunities
      </Button>
    </div>
  );
}

function WarningsDisplay({ warnings }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
      <div className="flex items-center gap-2 text-amber-600 mb-2">
        <AlertTriangle className="w-4 h-4" />
        <span className="font-medium">Notes</span>
      </div>
      <ul className="text-sm text-muted-foreground space-y-1">
        {warnings.map((w, i) => <li key={i}>• {w}</li>)}
      </ul>
    </div>
  );
}

function TieredOpportunities({ coreOpps, adjacentOpps, edgeOpps, activeTab, setActiveTab, expandedOpps, toggleOpp, addToBucketList, assetId, data }) {
  const tabs = [
    { id: 'core', label: 'Core', icon: Star, count: coreOpps.length, color: 'text-green-500', bgColor: 'bg-green-500/10' },
    { id: 'adjacent', label: 'Adjacent', icon: TrendingUp, count: adjacentOpps.length, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
    { id: 'edge', label: 'Edge / New Pages', icon: Lightbulb, count: edgeOpps.length, color: 'text-purple-500', bgColor: 'bg-purple-500/10' }
  ];

  const currentOpps = activeTab === 'core' ? coreOpps : activeTab === 'adjacent' ? adjacentOpps : edgeOpps;

  return (
    <div className="space-y-4">
      {/* Tier Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? `${tab.bgColor} ${tab.color}` 
                : 'text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded text-xs ${activeTab === tab.id ? 'bg-background/50' : 'bg-muted'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tier Description */}
      <TierDescription tier={activeTab} />

      {/* Opportunities List */}
      {currentOpps.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No {activeTab} opportunities found
        </div>
      ) : (
        <div className="space-y-4">
          {currentOpps.map((opp) => (
            <OpportunityCard
              key={opp.campaign_id || opp.opportunity_id}
              opp={opp}
              expanded={expandedOpps[opp.campaign_id || opp.opportunity_id]}
              onToggle={() => toggleOpp(opp.campaign_id || opp.opportunity_id)}
              addToBucketList={addToBucketList}
              assetId={assetId}
              data={data}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TierDescription({ tier }) {
  const descriptions = {
    core: {
      text: "High-confidence opportunities directly aligned with your main products/services",
      color: "text-green-600 bg-green-500/5 border-green-500/20"
    },
    adjacent: {
      text: "Common sub-cases and related segments worth exploring",
      color: "text-blue-600 bg-blue-500/5 border-blue-500/20"
    },
    edge: {
      text: "Niche segments that may need dedicated pages or more evidence",
      color: "text-purple-600 bg-purple-500/5 border-purple-500/20"
    }
  };

  const desc = descriptions[tier];
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${desc.color}`}>
      <Info className="w-4 h-4" />
      {desc.text}
    </div>
  );
}

function FlatOpportunities({ opportunities, expandedOpps, toggleOpp, addToBucketList, assetId, data }) {
  return (
    <div className="space-y-4">
      {opportunities.map((opp) => (
        <OpportunityCard
          key={opp.campaign_id || opp.opportunity_id}
          opp={opp}
          expanded={expandedOpps[opp.campaign_id || opp.opportunity_id]}
          onToggle={() => toggleOpp(opp.campaign_id || opp.opportunity_id)}
          addToBucketList={addToBucketList}
          assetId={assetId}
          data={data}
        />
      ))}
    </div>
  );
}

function OpportunityCard({ opp, expanded, onToggle, addToBucketList, assetId, data }) {
  const confidence = Math.round((opp.confidence || 0) * 100);
  const evidenceScore = opp.evidence_score || 0;
  const campaignabilityScore = opp.campaignability_score || 0;
  const tier = opp.tier || 'unknown';
  
  const tierColors = {
    core: 'border-green-500/30 bg-green-500/5',
    adjacent: 'border-blue-500/30 bg-blue-500/5',
    edge: 'border-purple-500/30 bg-purple-500/5',
    unknown: 'border-border'
  };

  const confColor = confidence >= 70 ? 'text-green-500' : confidence >= 50 ? 'text-amber-500' : 'text-red-500';
  const segment = opp.segment || {};

  // Get experiments and concerns for this opportunity
  const oppId = opp.campaign_id || opp.opportunity_id;
  const experiments = data?.experiments_by_campaign?.find(e => e.campaign_id === oppId)?.experiments || 
                      data?.experiments_by_opportunity?.find(e => e.opportunity_id === oppId)?.experiments || [];
  const concerns = data?.concerns_and_page_strategy?.find(c => c.opportunity_id === oppId)?.top_concerns || [];

  return (
    <div className={`border rounded-xl overflow-hidden ${tierColors[tier] || tierColors.unknown}`}>
      <div className="p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={onToggle}>
        <div className="flex items-start gap-3">
          <button className="mt-1">
            {expanded && <ChevronDown className="w-5 h-5" />}
            {!expanded && <ChevronRight className="w-5 h-5" />}
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h4 className="font-semibold text-foreground">{opp.campaign_name || opp.name || 'Untitled Campaign'}</h4>
              <span className={`text-sm font-medium ${confColor}`}>{confidence}%</span>
              {opp.confidence_cap_reason && (
                <span className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-600 rounded" title={opp.confidence_cap_reason}>
                  capped
                </span>
              )}
            </div>
            
            {/* Score breakdown */}
            <div className="flex flex-wrap gap-3 mb-2 text-xs">
              <ScoreBadge label="Evidence" score={evidenceScore} max={5} />
              <ScoreBadge label="Campaignability" score={campaignabilityScore} max={5} />
            </div>
            
            {/* Segment chips */}
            <div className="flex flex-wrap gap-2">
              <SegmentChip icon={Zap} text={segment.intent || 'N/A'} />
              <SegmentChip icon={Target} text={segment.stage || 'N/A'} />
              <SegmentChip icon={Users} text={segment.fit || 'N/A'} />
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              addToBucketList({
                item_type: 'opportunity',
                opportunity_id: opp.campaign_id || opp.opportunity_id,
                opportunity_name: opp.campaign_name || opp.name,
                asset_id: assetId
              });
            }}
          >
            <BookmarkPlus className="w-4 h-4" />
            Save
          </Button>
        </div>
      </div>

      {expanded && (
        <ExpandedContent 
          opp={opp} 
          experiments={experiments} 
          concerns={concerns}
          addToBucketList={addToBucketList}
          assetId={assetId}
        />
      )}
    </div>
  );
}

function ScoreBadge({ label, score, max }) {
  const percentage = (score / max) * 100;
  const color = percentage >= 60 ? 'text-green-600' : percentage >= 40 ? 'text-amber-600' : 'text-red-600';
  
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded">
      <span className="text-muted-foreground">{label}:</span>
      <span className={`font-medium ${color}`}>{score}/{max}</span>
    </div>
  );
}

function SegmentChip({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-xs text-muted-foreground">
      <Icon className="w-3 h-3" />{text}
    </span>
  );
}

function ExpandedContent({ opp, experiments, concerns, addToBucketList, assetId }) {
  const evidence = opp.evidence || [];
  const missing = opp.likely_missing || [];
  const supportedBy = opp.supported_by_groups || [];
  const subVariants = opp.sub_variants || [];

  return (
    <div className="border-t border-border">
      {/* Why it matters */}
      <div className="p-4 bg-muted/20">
        <p className="text-sm text-muted-foreground">{opp.why_it_matters}</p>
      </div>

      {/* Sub-variants */}
      {subVariants.length > 0 && (
        <div className="px-4 py-3 border-t border-border bg-muted/10">
          <h6 className="text-xs font-medium text-muted-foreground mb-2">Related Segments (merged)</h6>
          <div className="flex flex-wrap gap-1">
            {subVariants.map((v, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-muted rounded">{v}</span>
            ))}
          </div>
        </div>
      )}

      {/* Supported by (Asset references) */}
      {supportedBy.length > 0 && (
        <div className="px-4 py-3 border-t border-border">
          <h6 className="text-xs font-medium text-muted-foreground mb-2">Supported by Asset Groups</h6>
          <div className="flex flex-wrap gap-1">
            {supportedBy.map((ref, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded font-mono">{ref}</span>
            ))}
          </div>
        </div>
      )}

      {/* Evidence & Missing */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border">
        <EvidenceList title="Evidence" items={evidence} icon={CheckCircle} color="text-green-500" />
        <EvidenceList title="Missing" items={missing} icon={XCircle} color="text-red-500" />
      </div>

      {/* Experiments */}
      {experiments.length > 0 && (
        <ExperimentsSection 
          experiments={experiments} 
          opp={opp}
          addToBucketList={addToBucketList}
          assetId={assetId}
        />
      )}

      {/* Concerns */}
      {concerns.length > 0 && (
        <ConcernsSection concerns={concerns} />
      )}
    </div>
  );
}

function EvidenceList({ title, items, icon: Icon, color }) {
  if (!items || items.length === 0) return null;
  
  // Handle both string items (old format) and object items (v3.1 format)
  const getItemText = (item) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) return item.text || JSON.stringify(item);
    return String(item);
  };
  
  const getEvidenceStrength = (item) => {
    if (typeof item === 'object' && item !== null && item.evidence_strength) {
      return item.evidence_strength;
    }
    return null;
  };
  
  const strengthColors = {
    strong: 'bg-green-500/10 text-green-600',
    medium: 'bg-amber-500/10 text-amber-600',
    weak: 'bg-gray-500/10 text-gray-600'
  };
  
  return (
    <div>
      <h5 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        {title}
      </h5>
      <ul className="text-sm text-muted-foreground space-y-1.5">
        {items.map((item, i) => {
          const text = getItemText(item);
          const strength = getEvidenceStrength(item);
          return (
            <li key={i} className="flex items-start gap-2">
              <span>•</span>
              <span className="flex-1">{text}</span>
              {strength && (
                <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${strengthColors[strength] || ''}`}>
                  {strength}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ExperimentsSection({ experiments, opp, addToBucketList, assetId }) {
  return (
    <div className="border-t border-border p-4 bg-blue-500/5">
      <h5 className="font-medium text-foreground flex items-center gap-2 mb-3">
        <Beaker className="w-4 h-4 text-blue-500" />
        Experiments ({experiments.length})
      </h5>
      <div className="space-y-2">
        {experiments.map((exp) => (
          <ExperimentItem 
            key={exp.experiment_id} 
            exp={exp}
            opp={opp}
            addToBucketList={addToBucketList}
            assetId={assetId}
          />
        ))}
      </div>
    </div>
  );
}

function ExperimentItem({ exp, opp, addToBucketList, assetId }) {
  let riskColor = 'bg-red-500/10 text-red-600';
  if (exp.risk_level === 'low') riskColor = 'bg-green-500/10 text-green-600';
  else if (exp.risk_level === 'med') riskColor = 'bg-amber-500/10 text-amber-600';

  return (
    <div className="bg-surface border border-border rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-sm text-foreground">{exp.title}</span>
          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${riskColor}`}>
            {exp.risk_level}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addToBucketList({
            item_type: 'experiment',
            opportunity_id: opp.campaign_id || opp.opportunity_id,
            experiment_id: exp.experiment_id,
            opportunity_name: opp.campaign_name || opp.name,
            experiment_title: exp.title,
            asset_id: assetId
          })}
        >
          <BookmarkPlus className="w-4 h-4" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{exp.hypothesis}</p>
    </div>
  );
}

function ConcernsSection({ concerns }) {
  return (
    <div className="border-t border-border p-4">
      <h5 className="font-medium text-foreground flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        Concerns ({concerns.length})
      </h5>
      <div className="space-y-2">
        {concerns.map((concern, i) => (
          <ConcernItem key={i} concern={concern} />
        ))}
      </div>
    </div>
  );
}

function ConcernItem({ concern }) {
  let sevColor = 'bg-green-500/10 text-green-600';
  if (concern.severity === 'high') sevColor = 'bg-red-500/10 text-red-600';
  else if (concern.severity === 'med') sevColor = 'bg-amber-500/10 text-amber-600';

  const isNewPage = concern.address_with === 'new_page';
  const addrColor = isNewPage ? 'bg-purple-500/10 text-purple-600' : 'bg-blue-500/10 text-blue-600';
  const addrText = isNewPage ? 'New Page' : 'Personalize';

  const newPageRec = concern.new_page_recommendation;
  const showNewPage = newPageRec && newPageRec.needed;

  return (
    <div className="bg-muted/20 rounded-lg p-3">
      <div className="flex items-start gap-2 mb-1">
        <span className={`text-xs px-1.5 py-0.5 rounded ${sevColor}`}>{concern.severity}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${addrColor}`}>{addrText}</span>
      </div>
      <p className="text-sm text-foreground">{concern.concern}</p>
      {showNewPage && (
        <div className="mt-2 p-2 bg-purple-500/5 rounded border border-purple-500/20">
          <span className="text-xs font-medium text-purple-600">
            <FileText className="w-3 h-3 inline mr-1" />
            {newPageRec.page_name}
          </span>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// V3.1 COMPONENTS - Strategy Summary with Safe Action Types
// =============================================================================

function StrategySummaryPanel({ summary }) {
  const [expanded, setExpanded] = useState(false);
  
  // V3.1 format: strategy_summary.bullets with action_type
  const bullets = summary?.bullets || summary?.strategy_bullets || [];
  const topPriority = summary?.top_priority_campaigns || summary?.run_now_campaigns || [];
  const pagesToCreate = summary?.pages_to_create || [];
  const proofGaps = summary?.proof_gaps_to_fill || summary?.proof_gaps || [];
  
  const displayBullets = expanded ? bullets : bullets.slice(0, 3);

  // Action type to icon mapping
  const getActionIcon = (actionType) => {
    const icons = {
      'paid_acquisition': Megaphone,
      'retargeting': Target,
      'create_page': Plus,
      'personalize_page': Sparkles,
      'lean_into_differentiator': Star,
      'collect_proof': CheckCircle,
      'clarify_pricing': Zap,
      'comparison_module': TrendingUp,
      'trust_module': BadgeCheck
    };
    return icons[actionType] || ArrowRight;
  };

  return (
    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
            <ListChecks className="w-4 h-4 text-primary" />
          </div>
          <h3 className="font-semibold text-foreground">Strategy Summary</h3>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary hover:underline flex items-center gap-1">
          {expanded ? 'Show less' : 'Show all'}
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="space-y-2 mb-4">
        {displayBullets.map((bullet, i) => {
          const text = typeof bullet === 'string' ? bullet : bullet.text;
          const actionType = typeof bullet === 'object' ? bullet.action_type : null;
          const linkedPersonas = typeof bullet === 'object' ? bullet.linked_personas : [];
          const Icon = actionType ? getActionIcon(actionType) : ArrowRight;
          
          return (
            <div key={i} className="flex items-start gap-2 text-sm">
              <Icon className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="text-foreground">{text}</span>
                {linkedPersonas && linkedPersonas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {linkedPersonas.map((p, j) => (
                      <span 
                        key={j} 
                        className={`text-xs px-1.5 py-0.5 rounded ${
                          p.type === 'missing' 
                            ? 'bg-amber-500/10 text-amber-600' 
                            : 'bg-blue-500/10 text-blue-600'
                        }`}
                      >
                        {p.type === 'missing' && <UserPlus className="w-2 h-2 inline mr-0.5" />}
                        {p.persona_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {expanded && topPriority.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Top Priority</h4>
          <div className="flex flex-wrap gap-2">
            {topPriority.map((c, i) => {
              const name = typeof c === 'string' ? c : c.campaign_name || c;
              return (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-700 rounded-full text-sm">
                  <Zap className="w-3 h-3" />{name}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {expanded && pagesToCreate.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Pages to Create</h4>
          <div className="flex flex-wrap gap-2">
            {pagesToCreate.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-700 rounded-full text-sm">
                <Plus className="w-3 h-3" />{p.page_name}
                {p.serves_persona && <span className="text-xs opacity-75">({p.serves_persona})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {expanded && proofGaps.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Proof Gaps</h4>
          <div className="space-y-2">
            {proofGaps.map((g, i) => {
              const text = typeof g === 'string' ? g : `${g.gap_type}: ${g.detail}`;
              return (
                <div key={i} className="flex items-start gap-2 text-sm p-2 bg-amber-500/5 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="text-foreground">{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PersonaCoveragePanel({ existing, missing, campaignsByPersona }) {
  const existingPersonas = existing || [];
  const missingPersonas = missing || [];
  
  // Calculate campaign counts per persona
  const getCampaignCount = (personaName) => {
    const campaigns = campaignsByPersona[personaName] || [];
    return Array.isArray(campaigns) ? campaigns.length : 0;
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <Users className="w-4 h-4 text-blue-500" />
        </div>
        <h3 className="font-semibold text-foreground">Persona Coverage</h3>
        <span className="ml-auto text-sm text-muted-foreground">
          {existingPersonas.length} existing, {missingPersonas.length} missing
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Existing Personas */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1">
            <BadgeCheck className="w-3 h-3 text-green-500" />Currently Served
          </h4>
          {existingPersonas.length > 0 ? (
            <div className="space-y-2">
              {existingPersonas.map((p, i) => {
                const name = p.persona_name || p.name;
                const count = getCampaignCount(name);
                return (
                  <div key={i} className="flex items-center justify-between p-2 bg-green-500/5 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-foreground">{name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{count} campaigns</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No personas defined</p>
          )}
        </div>

        {/* Missing Personas */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1">
            <UserPlus className="w-3 h-3 text-amber-500" />Missing Personas
          </h4>
          {missingPersonas.length > 0 ? (
            <div className="space-y-2">
              {missingPersonas.slice(0, 4).map((p, i) => {
                const name = p.persona_name || p.name;
                const count = getCampaignCount(name);
                return (
                  <div key={i} className="p-2 bg-amber-500/5 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-foreground">{name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{count} campaigns</span>
                    </div>
                    {p.why_missing && <p className="text-xs text-muted-foreground mt-1 ml-6">{p.why_missing}</p>}
                    {p.recommended_action && (
                      <span className="inline-block mt-1 ml-6 text-xs px-2 py-0.5 bg-purple-500/10 text-purple-600 rounded">
                        {p.recommended_action}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">Good coverage!</p>
          )}
        </div>
      </div>
    </div>
  );
}

function CampaignPlanPanel({ opps, selectedPersona }) {
  const sorted = [...opps].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
  const top = sorted.slice(0, 8);

  // Get opportunity kind display
  const getKindBadge = (kind) => {
    if (kind === 'content_idea') return { text: 'Content Idea', class: 'bg-gray-500/10 text-gray-600' };
    if (kind === 'product_angle') return { text: 'Product Angle', class: 'bg-indigo-500/10 text-indigo-600' };
    return null; // Default campaign doesn't need badge
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5 overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
          <Megaphone className="w-4 h-4 text-violet-500" />
        </div>
        <h3 className="font-semibold text-foreground">Campaign Plan</h3>
        {selectedPersona !== 'all' && (
          <span className="ml-2 text-sm text-muted-foreground">
            (filtered: {selectedPersona})
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Campaign</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Persona</th>
              <th className="text-center py-2 px-3 font-medium text-muted-foreground">Coverage</th>
              <th className="text-center py-2 px-3 font-medium text-muted-foreground">Action</th>
              <th className="text-center py-2 px-3 font-medium text-muted-foreground">Priority</th>
            </tr>
          </thead>
          <tbody>
            {top.map((opp, i) => {
              const cov = opp.coverage_status || 'unknown';
              const pageAction = opp.page_action;
              const act = typeof pageAction === 'object' ? pageAction.type : (pageAction || 'PERSONALIZE');
              const personaRef = opp.persona_ref || opp.anchor_persona;
              const personaName = typeof personaRef === 'string' ? personaRef : personaRef?.persona_name || '—';
              const personaType = typeof personaRef === 'object' ? personaRef.type : 'existing';
              const kind = opp.opportunity_kind;
              const kindBadge = getKindBadge(kind);
              
              const covClass = cov === 'well_supported' ? 'bg-green-500/10 text-green-600' : cov === 'partially_supported' ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600';
              const actClass = act === 'NEW_PAGE' || act === 'CREATE_NEW_PAGE' ? 'bg-purple-500/10 text-purple-600' : 'bg-blue-500/10 text-blue-600';
              
              return (
                <tr key={opp.campaign_id || opp.opportunity_id || i} className="border-b border-border/50 hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">{i + 1}</span>
                      <div>
                        <span className="font-medium text-foreground">{opp.campaign_name || opp.name}</span>
                        {kindBadge && (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${kindBadge.class}`}>{kindBadge.text}</span>
                        )}
                        {opp.topic_tags && opp.topic_tags.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {opp.topic_tags.slice(0, 2).map((tag, j) => (
                              <span key={j} className="text-xs text-muted-foreground">#{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-sm ${personaType === 'missing' ? 'text-amber-600' : 'text-foreground'}`}>
                      {personaType === 'missing' && <UserPlus className="w-3 h-3 inline mr-1" />}
                      {personaName}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${covClass}`}>{cov.replace('_', ' ')}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${actClass}`}>
                      {act === 'NEW_PAGE' || act === 'CREATE_NEW_PAGE' ? 'New Page' : 'Personalize'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center font-medium text-foreground">{opp.priority_score || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
